<template>
  <div class="sd-card">
    <!-- Header with summary and tabs -->
    <div class="sd-chart-card__header m-0 p-4">
      <h3 class="sd-chart-card__title">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
        </svg>
        {{ $t('sales_dashboard.product_analytics') }}
      </h3>
      
      <div class="sd-tabs">
        <button 
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="sd-tabs__trigger"
          :class="{ 'sd-tabs__trigger--active': activeTab === tab.id }"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>
    
    <!-- Summary line -->
    <div class="px-4 pb-2">
      <p class="text-sm text-secondary-500">
        {{ uniqueProductCount }} {{ $t('sales_dashboard.products_sold_summary') }} • 
        {{ totalUnitsSold }} {{ $t('sales_dashboard.total_units_sold') }} • 
        <span class="font-semibold text-secondary-700">{{ formatCurrency(totalRevenue) }}</span> 
        {{ isShowingMRRRevenue ? $t('sales_dashboard.monthly_revenue_label') : $t('sales_dashboard.total_revenue_label') }}
        <span v-if="coverage && coverage.percentage < 100" class="text-secondary-400 ml-1" :title="coverageTooltip">
          ({{ coverage.percentage }}% {{ $t('sales_dashboard.product_data_coverage') }})
        </span>
      </p>
    </div>

    <!-- Tab Content -->
    <div class="p-4 pt-2">
      <!-- Overview Tab -->
      <div v-if="activeTab === 'overview'">
        <!-- Contract type toggle and sort -->
        <div class="flex flex-wrap justify-between items-center gap-3 mb-4">
          <div class="sd-tabs">
            <button 
              @click="contractTypeFilter = 'subscription'"
              class="sd-tabs__trigger flex items-center gap-1"
              :class="{ 'sd-tabs__trigger--active': contractTypeFilter === 'subscription' }"
            >
              {{ $t('sales_dashboard.rental_contracts') }}
              <span class="sd-badge sd-badge--primary">{{ subscriptionProductCount }}</span>
            </button>
            <button 
              @click="contractTypeFilter = 'cash'"
              class="sd-tabs__trigger flex items-center gap-1"
              :class="{ 'sd-tabs__trigger--active': contractTypeFilter === 'cash' }"
            >
              {{ $t('sales_dashboard.cash_services') }}
              <span class="sd-badge sd-badge--accent">{{ cashProductCount }}</span>
            </button>
          </div>
          
          <div class="flex gap-1">
            <button 
              v-for="sort in sortOptions"
              :key="sort.id"
              @click="sortBy = sort.id"
              :class="sortBy === sort.id ? 'bg-securdia-blue/10 text-securdia-blue border-securdia-blue/30' : 'bg-white text-secondary-600 border-secondary-200'"
              class="px-3 py-1 text-xs font-medium rounded-md border"
            >
              {{ sort.label }}
            </button>
          </div>
        </div>

        <!-- Summary stats cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div class="sd-kpi-card sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.total_sold') }}</p>
            <p class="sd-kpi-card__value text-secondary-800">{{ filteredTotalQuantity }} {{ $t('sales_dashboard.pieces_unit') }}</p>
          </div>
          <div class="sd-kpi-card sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.avg_price') }}</p>
            <p class="sd-kpi-card__value text-secondary-800">{{ formatCurrency(filteredAvgPrice) }}</p>
          </div>
          <div class="sd-kpi-card sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.avg_discount') }}</p>
            <p class="sd-kpi-card__value text-securdia-orange">{{ filteredAvgDiscount.toFixed(0) }}%</p>
          </div>
          <div class="sd-kpi-card sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ contractTypeFilter === 'subscription' ? 'Total MRR' : $t('sales_dashboard.total_revenue') }}</p>
            <p class="sd-kpi-card__value text-securdia-blue">{{ formatCurrency(filteredTotalRevenue) }}</p>
          </div>
        </div>

        <!-- Product rows -->
        <div class="space-y-2">
          <div 
            v-for="(product, index) in displayedProducts" 
            :key="product.name + '-' + index"
            class="bg-secondary-50 rounded-lg p-3 hover:bg-secondary-100 border border-transparent hover:border-securdia-blue/20"
          >
            <div class="flex items-start justify-between mb-2">
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-semibold text-secondary-800 truncate">{{ product.name }}</h4>
                <p class="text-xs text-secondary-500">{{ translateCategory(product.category) }} <span v-if="product.manufacturer">• {{ product.manufacturer }}</span></p>
              </div>
              <span class="text-sm font-bold text-securdia-blue ml-2">{{ formatCurrency(product.totalRevenue) }}</span>
            </div>
            
            <div class="grid grid-cols-4 gap-2 text-xs">
              <!-- Quantity with progress bar -->
              <div>
                <p class="text-secondary-500 mb-1">{{ $t('sales_dashboard.quantity_sold') }}</p>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-secondary-700">{{ product.unitsSold }} {{ $t('sales_dashboard.pieces_unit') }}</span>
                  <div class="sd-progress flex-1">
                    <div 
                      :style="{ width: (product.unitsSold / maxQuantity * 100) + '%' }"
                      class="sd-progress__fill sd-progress__fill--primary"
                    ></div>
                  </div>
                </div>
              </div>
              
              <!-- Average price with discount indicator -->
              <div>
                <p class="text-secondary-500 mb-1">{{ $t('sales_dashboard.avg_price') }}</p>
                <div class="flex items-center gap-1">
                  <span class="font-semibold text-secondary-700">{{ formatCurrency(product.avgPrice) }}</span>
                  <span v-if="product.avgDiscountPercent > 0" class="text-red-500 text-[10px]">
                    -{{ product.avgDiscountPercent.toFixed(0) }}%
                  </span>
                </div>
              </div>
              
              <!-- Price range -->
              <div>
                <p class="text-secondary-500 mb-1">{{ $t('sales_dashboard.price_range') }}</p>
                <span class="font-semibold text-secondary-700">
                  {{ formatCurrency(product.minPrice) }} - {{ formatCurrency(product.maxPrice) }}
                </span>
              </div>
              
              <!-- Total revenue / MRR -->
              <div>
                <p class="text-secondary-500 mb-1">{{ contractTypeFilter === 'subscription' ? 'MRR' : $t('sales_dashboard.total_revenue') }}</p>
                <span class="font-semibold text-secondary-700">{{ formatCurrency(product.totalRevenue) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="displayedProducts.length === 0" class="text-center py-8 text-secondary-500">
          {{ $t('sales_dashboard.no_data') }}
        </div>

        <button 
          v-if="sortedProducts.length > initialDisplayCount"
          @click="showAll = !showAll"
          class="mt-4 w-full text-sm text-securdia-blue hover:text-securdia-dark-blue flex items-center justify-center gap-1 py-2 border border-securdia-blue/20 rounded-lg hover:bg-securdia-blue/5 transition-all"
        >
          {{ showAll ? $t('sales_dashboard.show_less') : $t('sales_dashboard.show_all_products', { count: sortedProducts.length }) }}
          <svg :class="showAll ? 'rotate-180' : ''" class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>

        <!-- Top Selling Products Section -->
        <div class="mt-6 pt-6 border-t border-secondary-200">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Top 5 Rental Products -->
            <div class="sd-card overflow-hidden" style="background: linear-gradient(135deg, rgba(26, 141, 233, 0.05) 0%, white 100%);">
              <div class="p-3 border-b border-primary-100 bg-white/50">
                <div class="flex items-center gap-2">
                  <div class="w-7 h-7 bg-securdia-blue/10 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-secondary-800">{{ $t('sales_dashboard.top_selling_rental') }}</h4>
                    <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.top_5_rental') }}</p>
                  </div>
                </div>
              </div>
              
              <div class="p-3 flex flex-wrap gap-2">
                <div 
                  v-for="(product, index) in topRentalProducts" 
                  :key="'rental-' + index"
                  class="flex-1 min-w-[120px] p-2 bg-white rounded-lg border border-primary-100 hover:border-securdia-blue/50 transition-all hover:shadow-sm"
                >
                  <p class="text-xs font-semibold text-secondary-800 truncate">
                    <span class="sd-badge sd-badge--primary mr-1">{{ index + 1 }}</span>
                    {{ product.name }}
                  </p>
                  <p class="text-xs text-secondary-500 mt-1">{{ product.rentalUnits }} {{ $t('sales_dashboard.pieces_unit') }} • {{ translateCategory(product.category) }}</p>
                </div>
                
                <!-- Empty state -->
                <div v-if="topRentalProducts.length === 0" class="w-full text-center py-4 text-secondary-400 text-sm">
                  {{ $t('sales_dashboard.no_data') }}
                </div>
              </div>
            </div>

            <!-- Top 5 Cash Products -->
            <div class="sd-card overflow-hidden" style="background: linear-gradient(135deg, rgba(242, 145, 0, 0.05) 0%, white 100%);">
              <div class="p-3 border-b border-accent-100 bg-white/50">
                <div class="flex items-center gap-2">
                  <div class="w-7 h-7 bg-securdia-orange/10 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-securdia-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-secondary-800">{{ $t('sales_dashboard.top_selling_cash') }}</h4>
                    <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.top_5_cash') }}</p>
                  </div>
                </div>
              </div>
              
              <div class="p-3 flex flex-wrap gap-2">
                <div 
                  v-for="(product, index) in topCashProducts" 
                  :key="'cash-' + index"
                  class="flex-1 min-w-[120px] p-2 bg-white rounded-lg border border-accent-100 hover:border-securdia-orange/50 transition-all hover:shadow-sm"
                >
                  <p class="text-xs font-semibold text-secondary-800 truncate">
                    <span class="sd-badge sd-badge--accent mr-1">{{ index + 1 }}</span>
                    {{ product.name }}
                  </p>
                  <p class="text-xs text-secondary-500 mt-1">{{ product.cashUnits }} {{ $t('sales_dashboard.pieces_unit') }} • {{ translateCategory(product.category) }}</p>
                </div>
                
                <!-- Empty state -->
                <div v-if="topCashProducts.length === 0" class="w-full text-center py-4 text-secondary-400 text-sm">
                  {{ $t('sales_dashboard.no_data') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detailed Tab -->
      <div v-else-if="activeTab === 'detailed'">
        <!-- Search and filter -->
        <div class="flex flex-col md:flex-row gap-3 mb-4">
          <div class="flex-1 relative">
            <svg class="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <input 
              v-model="searchTerm"
              type="text"
              :placeholder="$t('sales_dashboard.search_products')"
              class="sd-select w-full pl-9"
              style="padding-left: 2.25rem;"
            >
          </div>
          
          <select 
            v-model="categoryFilter"
            class="sd-select"
          >
            <option value="all">{{ $t('sales_dashboard.all_categories') }}</option>
            <option v-for="cat in categories" :key="cat" :value="cat">{{ translateCategory(cat) }}</option>
          </select>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto rounded-lg border border-secondary-200">
          <table class="w-full text-sm">
            <thead class="bg-secondary-50 text-secondary-600">
              <tr>
                <th class="text-left py-3 px-3 font-semibold">{{ $t('sales_dashboard.product') }}</th>
                <th class="text-left py-3 px-3 font-semibold">{{ $t('sales_dashboard.category') }}</th>
                <th class="text-right py-3 px-3 font-semibold">{{ $t('sales_dashboard.quantity_sold') }}</th>
                <th class="text-right py-3 px-3 font-semibold">{{ $t('sales_dashboard.rental_column') }}</th>
                <th class="text-right py-3 px-3 font-semibold">{{ $t('sales_dashboard.cash_column') }}</th>
                <th class="text-right py-3 px-3 font-semibold">{{ $t('sales_dashboard.avg_discount') }}</th>
                <th class="text-right py-3 px-3 font-semibold">{{ $t('sales_dashboard.total_revenue') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-secondary-100">
              <tr 
                v-for="(product, idx) in filteredDetailedProducts" 
                :key="product.type || product.name + '-' + idx"
                class="hover:bg-secondary-50 transition-colors"
              >
                <td class="py-2.5 px-3 font-medium text-secondary-800">
                  {{ product.name }}
                  <span v-if="product.manufacturer" class="text-secondary-400 text-xs ml-1">{{ product.manufacturer }}</span>
                </td>
                <td class="py-2.5 px-3 text-secondary-600">
                  <span class="sd-badge sd-badge--primary">{{ translateCategory(product.category) }}</span>
                </td>
                <td class="py-2.5 px-3 text-right font-semibold">{{ product.unitsSold }}</td>
                <td class="py-2.5 px-3 text-right">
                  <span v-if="product.rentalUnits > 0">
                    {{ product.rentalUnits }} {{ $t('sales_dashboard.pieces_unit') }} • {{ formatCurrency(product.avgRentalPrice) }}{{ $t('sales_dashboard.per_month_short') }}
                  </span>
                  <span v-else class="text-secondary-400">-</span>
                </td>
                <td class="py-2.5 px-3 text-right">
                  <span v-if="product.cashUnits > 0">
                    {{ product.cashUnits }} {{ $t('sales_dashboard.pieces_unit') }} • {{ formatCurrency(product.avgCashPrice) }}
                  </span>
                  <span v-else class="text-secondary-400">-</span>
                </td>
                <td class="py-2 px-3 text-right">
                  <span :class="product.avgDiscountPercent > 15 ? 'text-red-600' : 'text-secondary-600'">
                    {{ product.avgDiscountPercent.toFixed(0) }}%
                  </span>
                </td>
                <td class="py-2 px-3 text-right font-medium">{{ formatCurrency(product.totalRentalRevenue + product.totalCashRevenue) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty state -->
        <div v-if="filteredDetailedProducts.length === 0" class="text-center py-8 text-secondary-500">
          {{ $t('sales_dashboard.no_data') }}
        </div>
      </div>

      <!-- Category Summary Tab -->
      <div v-else-if="activeTab === 'categories'">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div 
            v-for="cat in categoryStats" 
            :key="cat.category"
            class="p-4 bg-secondary-50 rounded-lg"
          >
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold text-secondary-700">{{ translateCategory(cat.category) }}</h4>
              <span class="px-2 py-1 bg-primary-100 text-securdia-dark-blue text-xs font-medium rounded-full">
                {{ cat.productCount }} {{ $t('sales_dashboard.products') }}
              </span>
            </div>
            
            <div class="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p class="text-secondary-500">{{ $t('sales_dashboard.units_sold') }}</p>
                <p class="font-bold text-secondary-800">{{ cat.unitsSold }}</p>
              </div>
              <div>
                <p class="text-secondary-500">MRR</p>
                <p class="font-bold text-secondary-800">{{ formatCurrency(cat.totalMRR) }}</p>
              </div>
              <div>
                <p class="text-secondary-500">{{ $t('sales_dashboard.total_revenue') }}</p>
                <p class="font-bold text-secondary-800">{{ formatCurrency(cat.totalRevenue) }}</p>
              </div>
              <div>
                <p class="text-secondary-500">{{ $t('sales_dashboard.share_of_sales') }}</p>
                <p class="font-bold text-secondary-800">{{ cat.sharePercent.toFixed(0) }}%</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="categoryStats.length === 0" class="text-center py-8 text-secondary-500">
          {{ $t('sales_dashboard.no_data') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductAnalytics',
  props: {
    productStats: {
      type: [Object, Array],
      default: () => ({ products: [], byCategory: [] })
    }
  },
  data() {
    return {
      activeTab: 'overview',
      contractTypeFilter: 'subscription',
      sortBy: 'quantity',
      showAll: false,
      searchTerm: '',
      categoryFilter: 'all',
      initialDisplayCount: 8
    };
  },
  computed: {
    tabs() {
      return [
        { id: 'overview', label: this.$t('sales_dashboard.overview') },
        { id: 'detailed', label: this.$t('sales_dashboard.detailed') },
        { id: 'categories', label: this.$t('sales_dashboard.by_category') }
      ];
    },
    
    sortOptions() {
      return [
        { id: 'quantity', label: this.$t('sales_dashboard.quantity') },
        { id: 'avgPrice', label: this.$t('sales_dashboard.price') },
        { id: 'revenue', label: this.$t('sales_dashboard.revenue') }
      ];
    },
    
    // Get products array from productStats
    products() {
      // New format: object with products array
      if (this.productStats?.products && Array.isArray(this.productStats.products)) {
        return this.productStats.products;
      }
      // Legacy format: direct array
      if (Array.isArray(this.productStats)) {
        return this.productStats;
      }
      // Fallback: try to extract product-like objects from values
      if (this.productStats && typeof this.productStats === 'object' && Object.keys(this.productStats).length > 0) {
        const values = Object.values(this.productStats).filter(p => p && typeof p === 'object' && p.name);
        if (values.length > 0) {
          return values;
        }
      }
      return [];
    },
    
    // Coverage info from API
    coverage() {
      return this.productStats?.coverage || null;
    },
    
    // Tooltip explaining coverage
    coverageTooltip() {
      if (!this.coverage) return '';
      return this.$t('sales_dashboard.coverage_tooltip', {
        withProducts: this.coverage.agreementsWithProducts,
        total: this.coverage.totalSubscriptionAgreements
      });
    },
    
    // Unique products sold count
    uniqueProductCount() {
      return this.products.length;
    },
    
    // Total units sold across all products
    totalUnitsSold() {
      return this.products.reduce((sum, p) => sum + (p.unitsSold || 0), 0);
    },
    
    // Total revenue from all sold products
    // Use totalContractRevenue if significant, otherwise fall back to MRR-based calculation
    totalRevenue() {
      const contractRevenue = this.products.reduce((sum, p) => 
        sum + (p.totalContractRevenue || 0) + (p.totalCashRevenue || 0), 0);
      
      // If contract revenue is very low compared to MRR, it means contract_period is missing on most agreements
      // Fall back to showing MRR (monthly) + cash as that's more meaningful
      const totalMRR = this.products.reduce((sum, p) => sum + (p.totalRentalRevenue || 0), 0);
      const totalCash = this.products.reduce((sum, p) => sum + (p.totalCashRevenue || 0), 0);
      
      // If contractRevenue is less than 3x MRR, contract_period data is likely incomplete
      // In that case, show MRR + cash instead (represents monthly recurring + one-time)
      if (contractRevenue < totalMRR * 3 && totalMRR > 0) {
        return totalMRR + totalCash;
      }
      
      return contractRevenue;
    },
    
    // Check if we're showing MRR-based revenue (for label purposes)
    isShowingMRRRevenue() {
      const contractRevenue = this.products.reduce((sum, p) => 
        sum + (p.totalContractRevenue || 0) + (p.totalCashRevenue || 0), 0);
      const totalMRR = this.products.reduce((sum, p) => sum + (p.totalRentalRevenue || 0), 0);
      return contractRevenue < totalMRR * 3 && totalMRR > 0;
    },
    
    // Products with rental units
    subscriptionProductCount() {
      return this.products.filter(p => p.rentalUnits > 0).length;
    },
    
    // Products with cash units
    cashProductCount() {
      return this.products.filter(p => p.cashUnits > 0).length;
    },
    
    // Filter by contract type (rental vs cash)
    filteredProducts() {
      if (this.contractTypeFilter === 'subscription') {
        return this.products.filter(p => p.rentalUnits > 0).map(p => ({
          ...p,
          unitsSold: p.rentalUnits,
          avgPrice: p.avgRentalPrice,
          minPrice: p.minRentalPrice,
          maxPrice: p.maxRentalPrice,
          totalRevenue: p.activeMRR || p.totalRentalRevenue
        }));
      } else {
        return this.products.filter(p => p.cashUnits > 0).map(p => ({
          ...p,
          unitsSold: p.cashUnits,
          avgPrice: p.avgCashPrice,
          minPrice: p.minCashPrice,
          maxPrice: p.maxCashPrice,
          totalRevenue: p.totalCashRevenue
        }));
      }
    },
    
    // Sort products
    sortedProducts() {
      return [...this.filteredProducts].sort((a, b) => {
        switch (this.sortBy) {
          case 'revenue':
            return b.totalRevenue - a.totalRevenue;
          case 'avgPrice':
            return b.avgPrice - a.avgPrice;
          case 'quantity':
          default:
            return b.unitsSold - a.unitsSold;
        }
      });
    },
    
    // Products to display (with show all toggle)
    displayedProducts() {
      return this.showAll ? this.sortedProducts : this.sortedProducts.slice(0, this.initialDisplayCount);
    },
    
    maxQuantity() {
      const quantities = this.sortedProducts.map(p => p.unitsSold);
      return Math.max(...quantities, 1);
    },
    
    // Filtered stats
    filteredTotalQuantity() {
      return this.filteredProducts.reduce((sum, p) => sum + p.unitsSold, 0);
    },
    
    filteredAvgPrice() {
      const total = this.filteredProducts.reduce((sum, p) => sum + (p.avgPrice * p.unitsSold), 0);
      const qty = this.filteredTotalQuantity;
      return qty > 0 ? total / qty : 0;
    },
    
    filteredAvgDiscount() {
      const products = this.filteredProducts.filter(p => p.avgDiscountPercent > 0);
      if (products.length === 0) return 0;
      return products.reduce((sum, p) => sum + p.avgDiscountPercent, 0) / products.length;
    },
    
    filteredTotalRevenue() {
      return this.filteredProducts.reduce((sum, p) => sum + p.totalRevenue, 0);
    },
    
    // Categories from products
    categories() {
      return [...new Set(this.products.map(p => p.category).filter(Boolean))];
    },
    
    // Filtered for detailed tab
    filteredDetailedProducts() {
      let prods = [...this.products];
      
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        prods = prods.filter(p => 
          p.name?.toLowerCase().includes(term) ||
          p.category?.toLowerCase().includes(term) ||
          p.manufacturer?.toLowerCase().includes(term)
        );
      }
      
      if (this.categoryFilter !== 'all') {
        prods = prods.filter(p => p.category === this.categoryFilter);
      }
      
      return prods.sort((a, b) => b.unitsSold - a.unitsSold);
    },
    
    // Category statistics from backend
    categoryStats() {
      const cats = this.productStats?.byCategory || [];
      const totalRevenue = cats.reduce((sum, c) => sum + c.totalRevenue, 0);
      
      return cats.map(c => ({
        ...c,
        productCount: c.products?.length || 0,
        sharePercent: totalRevenue > 0 ? (c.totalRevenue / totalRevenue) * 100 : 0
      }));
    },
    
    // Top 5 products by rental units
    topRentalProducts() {
      return [...this.products]
        .filter(p => p.rentalUnits > 0)
        .sort((a, b) => b.rentalUnits - a.rentalUnits)
        .slice(0, 5);
    },
    
    // Top 5 products by cash units
    topCashProducts() {
      return [...this.products]
        .filter(p => p.cashUnits > 0)
        .sort((a, b) => b.cashUnits - a.cashUnits)
        .slice(0, 5);
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    
    /**
     * Translate product category from database value to display name
     */
    translateCategory(category) {
      if (!category) return this.$t('sales_dashboard.category_unknown');

      const key = category.toLowerCase().replace(/[\s-]/g, '_');

      const translations = {
        'alarm': this.$t('sales_dashboard.category_alarm'),
        'personal_alarm': this.$t('sales_dashboard.category_alarm'),
        'ajax': this.$t('sales_dashboard.category_ajax'),
        'tag': this.$t('sales_dashboard.category_tag'),
        'display': this.$t('sales_dashboard.category_display'),
        'vehicle_tracker': this.$t('sales_dashboard.category_vehicle_tracker'),
        'software': this.$t('sales_dashboard.category_software'),
        'simcard': this.$t('sales_dashboard.category_simcard'),
        'accessory': this.$t('sales_dashboard.category_accessory'),
        'other': this.$t('sales_dashboard.category_other')
      };

      if (translations[key]) {
        return translations[key];
      }

      return category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ');
    },
    
    getRankClass(index) {
      switch(index) {
        case 0: return 'bg-yellow-400 text-yellow-900'; // Gold
        case 1: return 'bg-gray-300 text-gray-700'; // Silver
        case 2: return 'bg-orange-400 text-orange-900'; // Bronze
        default: return 'bg-secondary-200 text-secondary-600';
      }
    }
  }
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
